import Button from "@ui/button/Button"
import { EntityInput } from "./EntityInput"
import { askConfirm } from "./confirm"
import { Fragment, useRef, useState } from "react"
import { toast } from "react-toastify"
import Config from "src/Config"
import { superAdminFetch as fetch } from "src/services/superAdminFetch"
import { systemErrorMessage, systemPost } from "src/services/superAdminApi"
import { SuperAdminManualKyc } from "./SuperAdminManualKyc"
import { Callout, DangerZone, date, Disclosure, Fields, LinkButton, Muted, Page, Panel, Row, sentence, State, StatusBadge, Table } from "./adminUi"
import { UserOverviewData } from "./UserOverviewData"

// Backoffice KYC tooling (spec §3). Unlike the user-facing endpoints, provider
// names here are NOT masked — the issuer matrix returns real name + code.

interface IKycVerification {
	id: number
	uid: string
	user_uid: string
	company_uid: string
	module_reference: string | null
	status: string
	reason: string | null
	created_at: string
	resolved_at: string | null
	expires_at: string | null
	/** Session uid in the KYC module; null for old and manual verifications. */
	module_session?: string | null
}

interface IKycAttempt {
	id: number
	user_uid: string
	provider_id: number
	card_holder_uid: string | null
	kyc_verification_uid: string | null
	status: string
	reason: string | null
	reason_class: string | null
	created_at: string
	resolved_at: string | null
}

const NO_KYC_VERIFICATION = 2610

const KYC_STATUS_OPTIONS = [
	{ name: "Approved", value: "approved" },
	{ name: "Declined", value: "declined" },
	{ name: "Pending", value: "pending" },
	{ name: "Under review", value: "under_review" },
	{ name: "Expired", value: "expired" },
	{ name: "None", value: "none" },
]

const WASABI_INSTANCES = [
	{ name: "Sandbox (wasabi)", value: "wasabi" },
	{ name: "Live (wasabiprod)", value: "wasabiprod" },
]

export const SuperAdminKyc = ({ token }: { token: string }) => {
	const api = Config.scheme.URL_API

	const [userUid, setUserUid] = useState({ value: "" })
	const [companyUid, setCompanyUid] = useState({ value: "" })
	const manualRef = useRef<HTMLDivElement>(null)
	const [manualTarget, setManualTarget] = useState({ userUid: "", companyUid: "", revision: 0 })
	const [missingVerification, setMissingVerification] = useState<{ userUid: string; companyUid: string } | null>(null)
	const [overriding, setOverriding] = useState(false)
	const [loading, setLoading] = useState(false)

	const [verifications, setVerifications] = useState<IKycVerification[] | null>(null)
	const [attempts, setAttempts] = useState<IKycAttempt[] | null>(null)
	const [issuers, setIssuers] = useState<IKycIssuer[] | null>(null)

	// Overrides
	const [verificationOverride, setVerificationOverride] = useState({
		status: "",
		reason: "",
	})
	const [holderOverride, setHolderOverride] = useState({
		card_holder_uid: "",
		status: "",
		reason: "",
	})

	// Wasabi diagnose
	// wasabi = sandbox (local), wasabiprod = live (production) — default to
	// whichever instance is actually configured for this build.
	const [wasabiInstance, setWasabiInstance] = useState(
		Config.scheme.name === "live" ? "wasabiprod" : "wasabi",
	)
	const [wasabiResult, setWasabiResult] = useState<Record<string, unknown> | null>(null)
	const [wasabiLoading, setWasabiLoading] = useState(false)

	const [migrating, setMigrating] = useState(false)
	const [sessionChecks, setSessionChecks] = useState<Record<string, { loading?: boolean; value?: unknown }>>({})

	const copySession = async (session: string) => {
		try {
			await navigator.clipboard.writeText(session)
			toast.success("Session uid copied")
		} catch {
			toast.error("Could not copy. Allow clipboard access and try again")
		}
	}

	// session_check is the module's raw answer (or { error }); it is shown as JSON, never parsed.
	const checkSession = async (reference: string) => {
		setSessionChecks(current => ({ ...current, [reference]: { ...current[reference], loading: true } }))
		const response = await systemPost<{ session_check?: unknown }>(token, "system/kyc/module/session", { reference, check: true })
		if (response.error) toast.error("Module status: " + systemErrorMessage(response.error))
		setSessionChecks(current => ({ ...current, [reference]: { value: response.error ? current[reference]?.value : response.data.session_check ?? null } }))
	}

	const post = async (method: string, body: Record<string, unknown>) => {
		const res = await fetch(api + method, {
			method: "POST",
			headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
			body: JSON.stringify(body),
		})
		return res.json()
	}

	const loadForUser = async () => {
		const uid = userUid.value.trim()
		if (!uid) {
			toast.warning("Enter a user UID first")
			return
		}
		setLoading(true)
		try {
			const [ver, att, iss] = await Promise.all([
				post("system/kyc/verifications/list", {
					user_uid: uid,
					company_uid: companyUid.value.trim(),
				}),
				post("system/kyc/attempts/list", { user_uid: uid }),
				post("system/kyc/issuers", { user_uid: uid }),
			])
			setVerifications(Array.isArray(ver.data) ? ver.data : [])
			setAttempts(Array.isArray(att.data) ? att.data : [])
			setIssuers(Array.isArray(iss.data) ? iss.data : [])
			if (ver.error) toast.error("Verifications: " + (ver.error.message || "error"))
			if (att.error) toast.error("Attempts: " + (att.error.message || "error"))
			if (iss.error) toast.error("Issuers: " + (iss.error.message || "error"))
		} catch (e) {
			console.error(e)
			toast.error("Failed to load KYC data")
		} finally {
			setLoading(false)
		}
	}

	const confirmEmail = async () => {
		const uid = userUid.value.trim()
		if (!uid) return toast.warning("Enter a user UID first")
		const data = await post("system/user/email/confirm", { user_uid: uid })
		if (data.error) return toast.error("Confirm failed: " + (data.error.message || "error"))
		toast.success(
			data.data?.already_confirmed
				? "Email was already confirmed"
				: "Email confirmed",
		)
	}

	const overrideVerification = async () => {
		const uid = userUid.value.trim()
		if (!uid) return toast.warning("Enter a user UID first")
		setOverriding(true)
		setMissingVerification(null)
		const data = await systemPost(token, "system/kyc/verification/override", {
			user_uid: uid,
			status: verificationOverride.status,
			reason: verificationOverride.reason.trim() || undefined,
		})
		setOverriding(false)
		if (data.error) {
			if (Number(data.error.code) === NO_KYC_VERIFICATION) {
				setMissingVerification({ userUid: uid, companyUid: companyUid.value.trim() })
				return
			}
			return toast.error("Override failed: " + systemErrorMessage(data.error))
		}
		toast.success("Verification overridden")
		void loadForUser()
	}

	const overrideHolder = async () => {
		const holder = holderOverride.card_holder_uid.trim()
		if (!holder) return toast.warning("Enter a card holder UID")
		const data = await post("system/kyc/status/override", {
			card_holder_uid: holder,
			status: holderOverride.status,
			reason: holderOverride.reason.trim() || undefined,
		})
		if (data.error) return toast.error("Override failed: " + (data.error.message || "error"))
		toast.success("Provider verdict overridden")
		if (userUid.value.trim()) loadForUser()
	}

	const diagnoseWasabi = async () => {
		setWasabiLoading(true)
		setWasabiResult(null)
		try {
			const url = new URL(api + "system/wasabi/diagnose")
			url.searchParams.append("instance", wasabiInstance)
			const res = await fetch(url.toString(), {
				method: "GET",
				headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
			})
			const data = await res.json()
			if (data.error) {
				toast.error("Diagnose failed: " + (data.error.message || "error"))
				return
			}
			setWasabiResult(data.data ?? data)
		} catch (e) {
			console.error(e)
			toast.error("Diagnose error")
		} finally {
			setWasabiLoading(false)
		}
	}

	const runMigration = async () => {
		if (!await askConfirm({ title: "Run KYC registration migration?", body: "One-off backfill. Safe to run more than once: it skips users that are already migrated.", confirmLabel: "Run migration" })) return
		setMigrating(true)
		try {
			const data = await post("system/migrate/kyc-registration", {})
			if (data.error) {
				toast.error("Migration failed: " + (data.error.message || "error"))
				return
			}
			const applied = data.data?.applied
			toast.success(
				"Migration OK" +
					(Array.isArray(applied) ? ` (${applied.length} step(s) applied)` : ""),
			)
		} catch (e) {
			console.error(e)
			toast.error("Migration error")
		} finally {
			setMigrating(false)
		}
	}

	const overrideReady = Boolean(verificationOverride.status && verificationOverride.reason.trim())
	const holderReady = Boolean(holderOverride.card_holder_uid.trim() && holderOverride.status && holderOverride.reason.trim())

	return <Page>
		<Panel title='KYC lookup' hint='Find the user by name, e-mail or UID to see their verifications, provider attempts and the status each issuer holds. The company UID is optional and narrows the verifications.'>
			<Fields>
				<label>User<EntityInput token={token} kind='user' value={userUid.value} onChange={(value) => setUserUid({ value })} /></label>
				<label>Company (optional)<EntityInput token={token} kind='company' value={companyUid.value} onChange={(value) => setCompanyUid({ value })} /></label>
			</Fields>
			<Row style={{ marginTop: 16 }}>
				<Button onClick={loadForUser} disabled={loading} variant='primarySmall'>{loading ? "Loading…" : "Load KYC"}</Button>
			</Row>
		</Panel>

		{loading && !verifications && <State loading />}

		{verifications && <Panel title='Our KYC verifications' hint={`${verifications.length} found`}>
			{verifications.length === 0 ? <State empty emptyText='None found.' /> : <Table>
				<thead><tr><th>UID</th><th>Status</th><th>Reason</th><th>KYC session</th><th>Created</th><th>Resolved</th><th>Expires</th></tr></thead>
				<tbody>{verifications.map(v => <Fragment key={v.uid}><tr>
					<td><code>{v.uid}</code></td><td><StatusBadge value={v.status} /></td><td>{v.reason || <Muted>—</Muted>}</td>
					<td>{v.module_session ? <>
						<a href={Config.kycSessionUrl(v.module_session)} target='_blank' rel='noopener noreferrer' style={{ overflowWrap: "anywhere" }}><code>{v.module_session}</code></a>
						<Row style={{ marginTop: 6 }}>
							<LinkButton type='button' onClick={() => void copySession(v.module_session!)}>Copy</LinkButton>
							<LinkButton type='button' onClick={() => void checkSession(v.module_session!)} disabled={sessionChecks[v.module_session]?.loading}>{sessionChecks[v.module_session]?.loading ? "Checking…" : "Module status"}</LinkButton>
						</Row>
					</> : <Muted>—</Muted>}</td>
					<td>{date(v.created_at)}</td><td>{date(v.resolved_at)}</td><td>{date(v.expires_at)}</td>
				</tr>
				{v.module_session && sessionChecks[v.module_session]?.value !== undefined && <tr><td colSpan={7}>
					<Disclosure label='Module session status' defaultOpen><pre style={{ margin: 0, whiteSpace: "pre-wrap", overflowWrap: "anywhere", fontSize: 12 }}>{JSON.stringify(sessionChecks[v.module_session].value, null, 2)}</pre></Disclosure>
				</td></tr>}
				</Fragment>)}</tbody>
			</Table>}
		</Panel>}

		{attempts && <Panel title='Provider attempts' hint={`${attempts.length} found`}>
			{attempts.length === 0 ? <State empty emptyText='None found.' /> : <Table>
				<thead><tr><th>Provider ID</th><th>Card holder</th><th>Status</th><th>Reason type</th><th>Reason</th><th>Created</th></tr></thead>
				<tbody>{attempts.map(a => <tr key={a.id}>
					<td>{a.provider_id}</td><td>{a.card_holder_uid ? <code>{a.card_holder_uid}</code> : <Muted>—</Muted>}</td><td><StatusBadge value={a.status} /></td>
					<td>{a.reason_class ? sentence(a.reason_class) : <Muted>—</Muted>}</td><td>{a.reason || <Muted>—</Muted>}</td><td>{date(a.created_at)}</td>
				</tr>)}</tbody>
			</Table>}
		</Panel>}

		{issuers && <Panel title='Issuers' hint='Real provider names and the KYC status each issuer holds for this user.'>
			{issuers.length === 0 ? <State empty emptyText='No issuers.' /> : <Table>
				<thead><tr><th>Issuer</th><th>Code</th><th>KYC</th></tr></thead>
				<tbody>{issuers.map(iss => <tr key={iss.issuer}>
					<td><span style={{ fontWeight: 600 }}>{iss.name || `Issuer ${iss.issuer}`}</span><small>#{iss.issuer}</small></td>
					<td>{iss.code ? <code>{iss.code}</code> : <Muted>—</Muted>}</td>
					<td><Row style={{ gap: 14 }}>{iss.kyc.map(k => <span key={k.kind} style={{ display: "inline-flex", gap: 6, alignItems: "center" }}><Muted>{sentence(k.label || k.kind)}</Muted><StatusBadge value={k.status} /></span>)}</Row></td>
				</tr>)}</tbody>
			</Table>}
		</Panel>}

		<div ref={manualRef}><SuperAdminManualKyc key={manualTarget.revision} token={token} initialUserUid={manualTarget.userUid} initialCompanyUid={manualTarget.companyUid} /></div>

		<DangerZone title='Override KYC or email' hint="Changes a user's verification or email status. Use only after a manual document review.">
			<Panel title='Confirm email' hint='Marks the email of the user UID entered above as confirmed, without the user clicking the link.'>
				<Button onClick={confirmEmail} variant='secondarySmall' disabled={!userUid.value.trim()}>Confirm email for this user</Button>
			</Panel>
			<Panel title='Override our KYC verdict' hint='Forces our KYC verdict for the user above (latest verification). Approved lifts the private onboarding gate.'>
				<Fields>
					<label>New status<select value={verificationOverride.status} onChange={(event) => setVerificationOverride(p => ({ ...p, status: event.target.value }))}>
						<option value=''>Choose status…</option>
						{KYC_STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.name}</option>)}
					</select></label>
					<label>Reason<input value={verificationOverride.reason} onChange={(event) => setVerificationOverride(p => ({ ...p, reason: event.target.value }))} placeholder='Why the verdict is being changed' /></label>
				</Fields>
				<Row style={{ marginTop: 16 }}><Button onClick={overrideVerification} disabled={overriding || !overrideReady} variant='primarySmall'>{overriding ? "Applying…" : "Apply to user"}</Button></Row>
				{missingVerification && <Callout role='alert' tone='warn' style={{ marginTop: 14 }}>
					<span>{`User ${missingVerification.userUid} has no KYC verification to override. Create it through manual KYC, then submit the holder to the issuer.`}</span>
					<Row style={{ marginTop: 6 }}><Button variant='secondarySmall' onClick={() => {
						setManualTarget(current => ({ ...missingVerification, revision: current.revision + 1 }))
						manualRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
					}}>Open manual KYC</Button></Row>
				</Callout>}
			</Panel>
			<Panel title='Override issuer KYC' hint='Changes issuer KYC for this card holder only. This does not approve our user KYC or the issuer audit.'>
				<Fields>
					<label>Card holder<EntityInput token={token} kind='holder' value={holderOverride.card_holder_uid} onChange={(value) => setHolderOverride(p => ({ ...p, card_holder_uid: value }))} /></label>
					<label>New status<select value={holderOverride.status} onChange={(event) => setHolderOverride(p => ({ ...p, status: event.target.value }))}>
						<option value=''>Choose status…</option>
						{KYC_STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.name}</option>)}
					</select></label>
					<label>Reason<input value={holderOverride.reason} onChange={(event) => setHolderOverride(p => ({ ...p, reason: event.target.value }))} placeholder='e.g. Manually approved after document review' /></label>
				</Fields>
				<Row style={{ marginTop: 16 }}><Button onClick={overrideHolder} disabled={!holderReady} variant='primarySmall'>Apply to holder</Button></Row>
			</Panel>
		</DangerZone>

		<DangerZone title='Engineering tools' hint='For developers. Do not use during support work.'>
			<Panel title='Wasabi signature diagnose' hint="Compare the derived public key with the portal's public key. A 403 on production means the server IP is not on Wasabi's whitelist.">
				<Fields>
					<label>Instance<select value={wasabiInstance} onChange={(event) => setWasabiInstance(event.target.value || "wasabi")}>
						{WASABI_INSTANCES.map(o => <option key={o.value} value={o.value}>{o.name}</option>)}
					</select></label>
				</Fields>
				<Row style={{ marginTop: 16 }}><Button onClick={diagnoseWasabi} disabled={wasabiLoading} variant='secondarySmall'>{wasabiLoading ? "Checking…" : "Run diagnose"}</Button></Row>
				{wasabiResult && <div style={{ marginTop: 14 }}><UserOverviewData value={wasabiResult} /></div>}
			</Panel>
			<Panel title='Feature migration' hint='One-off, safe-to-repeat migration for the registration and KYC feature (creates the KYC tables, adds columns, backfills).'>
				<Row><Button onClick={runMigration} disabled={migrating} variant='secondarySmall'>{migrating ? "Running…" : "Run KYC registration migration"}</Button></Row>
			</Panel>
		</DangerZone>
	</Page>
}
